import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { authClient } from "@/lib/auth-client";

const MIN_PASSWORD_LENGTH = 8;

const signInSchema = z.object({
  password: z.string().min(MIN_PASSWORD_LENGTH),
});

const signUpSchema = z
  .object({
    name: z.string().min(2),
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH)
      .regex(/[A-Z]/)
      .regex(/[a-z]/)
      .regex(/[0-9]/)
      .regex(/[^A-Za-z0-9]/),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
  });

type InvitationAuthProps = {
  invitationId: string;
  email: string;
};

export function InvitationAuth({ invitationId, email }: InvitationAuthProps) {
  return (
    <Tabs className="w-full" defaultValue="sign-in">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="sign-in">Iniciar sesión</TabsTrigger>
        <TabsTrigger value="sign-up">Crear cuenta</TabsTrigger>
      </TabsList>
      <TabsContent value="sign-in">
        <SignInInline email={email} invitationId={invitationId} />
      </TabsContent>
      <TabsContent value="sign-up">
        <SignUpInline email={email} />
      </TabsContent>
    </Tabs>
  );
}

function SignInInline({ email, invitationId }: { email: string; invitationId: string }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const parsed = signInSchema.safeParse({ password });
      if (!parsed.success) {
        toast.error("La contraseña debe tener al menos 8 caracteres");
        return;
      }
      setIsLoading(true);
      await authClient.signIn.email(
        { email, password },
        {
          onSuccess(ctx: { data: { twoFactorRedirect?: boolean } }) {
            if (ctx.data.twoFactorRedirect) {
              navigate(
                `/auth/restaurant/sign-in/two-factor?redirect=/accept-invitation/${invitationId}`,
              );
            }
          },
          onError(err: { error: { message: string } }) {
            toast.error(err.error.message);
          },
          onResponse() {
            setIsLoading(false);
          },
        },
      );
    },
    [email, password, invitationId, navigate],
  );

  return (
    <form className="space-y-4 pt-4" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="si-email">Email</Label>
        <Input disabled id="si-email" type="email" value={email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="si-password">Contraseña</Label>
        <div className="relative">
          <Input
            autoComplete="current-password"
            className="pr-10"
            disabled={isLoading}
            id="si-password"
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            value={password}
          />
          <button
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            className="-translate-y-1/2 absolute top-1/2 right-3 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
            type="button"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <Button className="w-full" disabled={isLoading} type="submit">
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Iniciar sesión
      </Button>
    </form>
  );
}

function SignUpInline({ email }: { email: string }) {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const parsed = signUpSchema.safeParse({ name, password, confirmPassword });
      if (!parsed.success) {
        toast.error(
          "Revisá los datos: nombre mínimo 2 caracteres, contraseña 8+ con mayúscula, minúscula, número y símbolo, y confirmación coincidente.",
        );
        return;
      }
      setIsLoading(true);
      try {
        await authClient.signUp.email({ name, email, password });
        toast.success("Cuenta creada");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "No se pudo crear la cuenta");
      } finally {
        setIsLoading(false);
      }
    },
    [name, email, password, confirmPassword],
  );

  return (
    <form className="space-y-4 pt-4" onSubmit={onSubmit}>
      <div className="space-y-2">
        <Label htmlFor="su-name">Nombre completo</Label>
        <Input
          autoComplete="name"
          disabled={isLoading}
          id="su-name"
          onChange={(e) => setName(e.target.value)}
          value={name}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="su-email">Email</Label>
        <Input disabled id="su-email" type="email" value={email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="su-password">Contraseña</Label>
        <div className="relative">
          <Input
            autoComplete="new-password"
            className="pr-10"
            disabled={isLoading}
            id="su-password"
            onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? "text" : "password"}
            value={password}
          />
          <button
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            className="-translate-y-1/2 absolute top-1/2 right-3 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
            type="button"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-muted-foreground text-xs">
          Mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="su-confirm">Confirmar contraseña</Label>
        <Input
          autoComplete="new-password"
          disabled={isLoading}
          id="su-confirm"
          onChange={(e) => setConfirmPassword(e.target.value)}
          type={showPassword ? "text" : "password"}
          value={confirmPassword}
        />
      </div>
      <Button className="w-full" disabled={isLoading} type="submit">
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Crear cuenta y aceptar invitación
      </Button>
    </form>
  );
}
